import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Card, Typography, Spin, Alert, Tag, Divider, Space, List, Descriptions, Badge, Empty, Grid, Image, Table } from 'antd';
import { 
  FileTextOutlined, 
  UserOutlined, 
  CalendarOutlined, 
  MedicineBoxOutlined, 
  InfoCircleOutlined,
  EnvironmentOutlined,
  IdcardOutlined,
  ExperimentOutlined,
  PictureOutlined
} from '@ant-design/icons';
import api from '../services/api';
import dayjs from 'dayjs';

const { Title, Text, Paragraph } = Typography;
const { useBreakpoint } = Grid;

interface ClsHistoryItem {
  maKcb: string;
  hoTen: string;
  tuoi: string;
  diaChi: string;
  maHh: number;
  maThanhToanCt: number;
  tenDichVu: string;
  maLoaiDv: number;
  doKetQua: string;
  nguoiDoc: string;
  nguoiGui: string;
  ngayLam: string;
  ngayTraKq: string;
  details: ClsDetail[];
}

interface ClsDetail {
  maThanhToanCt: number;
  maHh: number;
  tenDichVu: string;
  maChiSo: number | null;
  tenChiSo: string;
  ketQua: string;
  daiGiaTri: string;
  donViTinh: string;
  doKetQua: string;
  ngayLam: string;
  ngayTraKQ: string;
}

interface ClsImage {
  maThanhToanCt: number;
  maHh: number;
  tenDichVu: string;
  thuTu: number;
  anh: string; // Base64 string from API
}

const groupConfig: Record<string, { color: string; icon: React.ReactNode }> = {
  'X-Quang': { color: 'blue', icon: <FileTextOutlined /> },
  'Nội soi': { color: 'cyan', icon: <MedicineBoxOutlined /> },
  'Chụp CT': { color: 'purple', icon: <FileTextOutlined /> },
  'Điện tim': { color: 'red', icon: <FileTextOutlined /> },
  'Thăm dò chức năng': { color: 'orange', icon: <InfoCircleOutlined /> },
  'Xét nghiệm': { color: 'green', icon: <ExperimentOutlined /> },
  'Siêu âm': { color: 'geekblue', icon: <MedicineBoxOutlined /> },
  'Chụp MRI': { color: 'volcano', icon: <FileTextOutlined /> },
  'Khác': { color: 'default', icon: <FileTextOutlined /> }
};

const groupMapping: Record<number, string> = {
  2: 'X-Quang',
  3: 'Nội soi',
  4: 'Chụp CT',
  5: 'Điện tim',
  15: 'Thăm dò chức năng',
  20: 'Xét nghiệm',
  21: 'Xét nghiệm',
  22: 'Xét nghiệm',
  23: 'Siêu âm',
  24: 'Xét nghiệm',
  26: 'Siêu âm',
  28: 'Chụp MRI',
  12: 'Khác'
};

const LichSuCls: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<ClsHistoryItem[]>([]);
  const [images, setImages] = useState<ClsImage[]>([]);
  const [error, setError] = useState<string | null>(null);
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  useEffect(() => {
    const fetchData = async () => {
      console.log('[LichSuCls Frontend] Token từ URL:', token);
      if (!token) {
        setError('Vui lòng cung cấp mã khám bệnh');
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        const apiUrl = `/lich-su-cls/${token}`;
        console.log('[LichSuCls Frontend] Gọi API:', apiUrl);
        const response = await api.get(apiUrl);
        console.log('[LichSuCls Frontend] Kết quả API:', response.data);
        setData(response.data.history || []);
        setImages(response.data.images || []);
      } catch (err) {
        console.error('[LichSuCls Frontend] Lỗi API:', err);
        setError('Không thể tải dữ liệu lịch sử cận lâm sàng');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token]);

  const groupData = (items: ClsHistoryItem[]) => {
    const groups: Record<string, ClsHistoryItem[]> = {};
    items.forEach(item => {
      const groupName = groupMapping[item.maLoaiDv] || 'Khác';
      if (!groups[groupName]) {
        groups[groupName] = [];
      }
      groups[groupName].push(item);
    });
    return groups;
  };

  const groupedData = groupData(data);

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', gap: 16 }}>
      <Spin size="large" />
      <Text type="secondary">Đang tải kết quả cận lâm sàng...</Text>
    </div>
  );

  if (error) return (
    <div style={{ padding: 24 }}>
      <Alert message="Thông báo" description={error} type="error" showIcon />
    </div>
  );

  const patientInfo = data[0];

  return (
    <div style={{ padding: isMobile ? '12px' : '24px', maxWidth: 1200, margin: '0 auto' }}>
      {/* Thông tin bệnh nhân */}
      <Card 
        bordered={false}
        style={{ 
          marginBottom: 24, 
          borderRadius: 12, 
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          background: 'linear-gradient(135deg, #ffffff 0%, #f0f5ff 100%)'
        }}
      >
        <Title level={isMobile ? 4 : 3} style={{ marginBottom: 20, color: '#1d39c4' }}>
          <Space>
            <IdcardOutlined />
            <span>LỊCH SỬ CẬN LÂM SÀNG</span>
          </Space>
        </Title>
        
        {patientInfo ? (
          <Descriptions 
            bordered 
            size={isMobile ? "small" : "middle"}
            column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }}
            contentStyle={{ background: '#fff' }}
          >
            <Descriptions.Item label={<Space><UserOutlined /> Họ tên</Space>}>
              <Text strong style={{ fontSize: 16, wordBreak: 'break-word' }}>{patientInfo.hoTen}</Text>
            </Descriptions.Item>
            <Descriptions.Item label={<Space><CalendarOutlined /> Tuổi</Space>}>
              {patientInfo.tuoi}
            </Descriptions.Item>
            <Descriptions.Item label={<Space><FileTextOutlined /> Mã KCB</Space>}>
              <Tag color="blue" style={{ fontSize: 14 }}>{patientInfo.maKcb}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label={<Space><EnvironmentOutlined /> Địa chỉ</Space>}>
              {patientInfo.diaChi}
            </Descriptions.Item>
          </Descriptions>
        ) : (
          <Empty description="Không tìm thấy thông tin bệnh nhân" />
        )}
      </Card>

      {/* Danh sách kết quả theo nhóm */}
      {Object.keys(groupedData).length === 0 ? (
        <Empty description="Không có dữ liệu kết quả cận lâm sàng" />
      ) : (
        Object.entries(groupedData).map(([groupName, items]) => {
          const config = groupConfig[groupName] || groupConfig['Khác'];
          return (
            <div key={groupName} style={{ marginBottom: 32 }}>
              <Divider orientation="left" style={{ margin: '16px 0' }}>
                <Space>
                  <Badge color={config.color} />
                  <Title level={4} style={{ margin: 0, color: '#434343' }}>
                    {groupName} <Text type="secondary" style={{ fontSize: 14, fontWeight: 'normal' }}>({items.length})</Text>
                  </Title>
                </Space>
              </Divider>

              <List
                grid={{ gutter: 16, xs: 1, sm: 1, md: 1, lg: 1, xl: 2, xxl: 2 }}
                dataSource={items}
                renderItem={(item) => (
                  <List.Item>
                    <Card 
                      hoverable
                      size="small"
                      style={{ 
                        borderRadius: 8, 
                        borderLeft: `4px solid ${config.color === 'default' ? '#d9d9d9' : config.color}`,
                        height: '100%'
                      }}
                    >
                      {/* CUSTOM TITLE SECTION - NO MORE ISSUES! */}
                      <div style={{ paddingBottom: '12px', marginBottom: '12px', borderBottom: '1px solid #f0f0f0' }}>
                        <div style={{ 
                          display: 'flex', 
                          flexDirection: isMobile ? 'column' : 'row',
                          justifyContent: 'flex-start', 
                          alignItems: isMobile ? 'flex-start' : 'center', 
                          gap: '8px', 
                          width: '100%'
                        }}>
                          <Text strong style={{ 
                            fontSize: isMobile ? 16 : 17, 
                            wordBreak: 'break-word',
                            overflowWrap: 'anywhere',
                            width: '100%',
                            lineHeight: 1.4,
                            display: 'block',
                            color: '#1f1f1f'
                          }}>
                            {item.tenDichVu}
                          </Text>
                          <Tag color={config.color} style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>
                            {groupName}
                          </Tag>
                        </div>
                      </div>
                    
                      <Space direction="vertical" style={{ width: '100%' }} size="small">
                        {/* Thời gian & Bác sĩ */}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px', marginBottom: 8 }}>
                          <Space>
                            <CalendarOutlined style={{ color: '#8c8c8c' }} />
                            <Text type="secondary" style={{ fontSize: 13 }}>
                              {item.ngayLam ? dayjs(item.ngayLam).format('DD/MM/YYYY HH:mm') : 'N/A'}
                            </Text>
                          </Space>
                          <Space>
                            <UserOutlined style={{ color: '#8c8c8c' }} />
                            <Text type="secondary" style={{ fontSize: 13 }}>
                              BS: {item.nguoiDoc || 'Đang cập nhật'}
                            </Text>
                          </Space>
                        </div>

                        {/* Mô tả chi tiết (Nội soi/Siêu âm) */}
                        {item.doKetQua && (
                          <div style={{ background: '#f5f5f5', padding: '10px', borderRadius: 6, marginBottom: 8 }}>
                            <Text strong style={{ fontSize: 14, display: 'block', marginBottom: 4 }}>
                              <FileTextOutlined /> Mô tả chi tiết:
                            </Text>
                            <Paragraph 
                              style={{ 
                                margin: 0, 
                                whiteSpace: 'pre-wrap', 
                                fontSize: 15.5, 
                                wordBreak: 'break-word',
                                lineHeight: 1.7
                              }}
                            >
                              {item.doKetQua}
                            </Paragraph>
                          </div>
                        )}

                        {/* Danh sách chỉ số xét nghiệm / Kết luận */}
                        {item.details && item.details.length > 0 && (
                          <div style={{ background: '#f0f9ff', padding: '10px', borderRadius: 6, border: '1px solid #bae6fd', marginBottom: 8, overflow: 'hidden' }}>
                            <Text strong style={{ fontSize: 14, color: '#0369a1', display: 'block', marginBottom: 8 }}>
                              <ExperimentOutlined /> Kết quả:
                            </Text>
                            
                            {/* Phần chỉ số xét nghiệm (có tenChiSo) */}
                            {item.details.some(d => d.tenChiSo && d.tenChiSo.trim() !== '') && (
                              <Table
                                size="small"
                                pagination={false}
                                dataSource={item.details.filter(d => d.tenChiSo && d.tenChiSo.trim() !== '')}
                                rowKey={(record) => `${record.maChiSo || record.tenChiSo}`}
                                scroll={isMobile ? { x: 400 } : undefined}
                                style={{ marginBottom: 8 }}
                                columns={[
                                  {
                                    title: 'Tên chỉ số',
                                    dataIndex: 'tenChiSo',
                                    key: 'tenChiSo',
                                    width: '30%',
                                    render: (text) => (
                                      <Text style={{ color: '#374151', fontWeight: 500, wordBreak: 'break-word' }}>
                                        {text}
                                      </Text>
                                    )
                                  },
                                  {
                                    title: 'Kết quả',
                                    dataIndex: 'ketQua',
                                    key: 'ketQua',
                                    width: '20%',
                                    render: (text) => text ? (
                                      <Tag color={isMobile ? "blue" : "green"} style={{ fontWeight: 'bold', wordBreak: 'break-word', fontSize: 14 }}>
                                        {text}
                                      </Tag>
                                    ) : null
                                  },
                                  {
                                    title: 'Đơn vị',
                                    dataIndex: 'donViTinh',
                                    key: 'donViTinh',
                                    width: '15%',
                                    render: (text) => text ? (
                                      <Text type="secondary" style={{ fontSize: 13.5, color: 'black' }}>{text}</Text>
                                    ) : null
                                  },
                                  {
                                    title: 'Chỉ số bình thường',
                                    dataIndex: 'daiGiaTri',
                                    key: 'daiGiaTri',
                                    width: '35%',
                                    render: (text) => text ? (
                                      <Text type="secondary" style={{ fontSize: 13.5, fontStyle: 'italic', wordBreak: 'break-word', color: 'black' }}>({text})</Text>
                                    ) : null
                                  }
                                ]}
                              />
                            )}
                            
                            {/* Phần kết luận dài (không có tenChiSo) */}
                            {item.details.filter(d => !d.tenChiSo || d.tenChiSo.trim() === '').map((detail, idx) => (
                              <div key={idx} style={{ padding: '8px 0' }}>
                                <div style={{ 
                                  background: '#fff7e6', 
                                  padding: '10px', 
                                  borderRadius: 6, 
                                  border: '1px solid #ffe7ba',
                                  width: '100%'
                                }}>
                                  <Paragraph 
                                    style={{ 
                                      margin: 0, 
                                      whiteSpace: 'pre-wrap', 
                                      fontSize: 14, 
                                      wordBreak: 'break-word',
                                      fontWeight: 'bold',
                                      color: '#873800'
                                    }}
                                  >
                                    {detail.ketQua}
                                  </Paragraph>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}

                        {/* Hình ảnh đính kèm */}
                        {images.filter(img => img.maThanhToanCt === item.maThanhToanCt).length > 0 && (
                          <div style={{ marginTop: 12 }}>
                            <Divider plain style={{ margin: '8px 0', fontSize: 13 }}>
                              <Space><PictureOutlined /> Hình ảnh kết quả</Space>
                            </Divider>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                              <Image.PreviewGroup>
                                {images
                                  .filter(img => img.maThanhToanCt === item.maThanhToanCt)
                                  .sort((a, b) => a.thuTu - b.thuTu)
                                  .map((img, idx) => (
                                    <Image
                                      key={`${img.maThanhToanCt}-${idx}`}
                                      width={isMobile ? 80 : 120}
                                      height={isMobile ? 80 : 120}
                                      style={{ borderRadius: 4, objectFit: 'cover' }}
                                      src={`data:image/jpeg;base64,${img.anh}`}
                                      fallback="https://via.placeholder.com/150?text=Error"
                                      loading="lazy"
                                      placeholder={<div style={{ width: isMobile ? 80 : 120, height: isMobile ? 80 : 120, background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Spin size="small" /></div>}
                                    />
                                  ))
                                }
                              </Image.PreviewGroup>
                            </div>
                          </div>
                        )}
                      </Space>
                    </Card>
                  </List.Item>
                )}
              />
            </div>
          );
        })
      )}
    </div>
  );
};

export default LichSuCls;
